'use client';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';
import { Sidfot } from '../landning/komponenter/Sidfot';
import { Sidhuvud } from '../landning/komponenter/Sidhuvud';
import type { JuridiskDokument } from './juridisk-dokument';

type Props = {
    dokument: JuridiskDokument;
    /**
     * Det andra juridiska dokumentet, som en länk sist på sidan. Texten står i bestämd form
     * ("integritetspolicyn") eftersom den läses mitt i en mening — en titel i obestämd form
     * blir "Läs också integritetspolicy".
     */
    syskon: { text: string; href: string };
};

/**
 * Renderar ett juridiskt dokument som publik sida.
 *
 * Samma skal som landningssidan — samma sidhuvud, samma sidfot, appens eget MUI-tema — så att
 * den som klickar på "Användarvillkor" i registreringsrutan inte landar i en annan produkt.
 * Innehållet är rent textflöde i en spalt med läsbar radlängd, och en innehållsförteckning
 * som ligger kvar vid sidan på större skärmar: ett avtal läses inte linjärt, det slås i.
 */
export function JuridiskSidaView({ dokument, syskon }: Props) {
    return (
        <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default', color: 'text.primary' }}>
            <Box component="a" href="#innehall" sx={visuelltDoldTillsFokus}>
                Hoppa till innehållet
            </Box>
            <Sidhuvud />

            <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 8, md: 12 } }}>
                <Box component="header" sx={{ maxWidth: '46rem' }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                        Juridik
                    </Typography>
                    <Typography component="h1" variant="h2" sx={{ mt: 1, textWrap: 'balance' }}>
                        {dokument.titel}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                        {dokument.ingress}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.disabled' }}>
                        Senast ändrad {dokument.senastÄndrad}
                    </Typography>
                </Box>

                {dokument.förbehåll && (
                    <Alert severity="info" sx={{ mt: 4, maxWidth: '46rem' }}>
                        {dokument.förbehåll}
                    </Alert>
                )}

                <Box
                    sx={{
                        mt: { xs: 5, md: 7 },
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row-reverse' },
                        // space-between, annars packas spalterna mot högerkanten i row-reverse
                        // och brödtexten blir indragen i förhållande till rubriken ovanför.
                        justifyContent: 'space-between',
                        // stretch i mobilläget: med flex-start får spalten bredd efter sitt
                        // innehåll, och underbiträdestabellens min-bredd sköt ut hela sidan
                        // i sidled i stället för att rulla inuti sin egen behållare.
                        alignItems: { xs: 'stretch', md: 'flex-start' },
                        gap: { xs: 4, md: 8 }
                    }}
                >
                    <Box
                        component="nav"
                        aria-label={`Innehåll i ${dokument.titel.toLowerCase()}`}
                        sx={{
                            width: { xs: '100%', md: 260 },
                            flexShrink: 0,
                            position: { md: 'sticky' },
                            top: { md: 96 }
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                            Innehåll
                        </Typography>
                        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
                            {dokument.avsnitt.map((avsnitt, index) => (
                                <li key={avsnitt.id}>
                                    <Link
                                        href={`#${avsnitt.id}`}
                                        variant="body2"
                                        color="text.secondary"
                                        underline="hover"
                                    >
                                        {index + 1}. {avsnitt.rubrik}
                                    </Link>
                                </li>
                            ))}
                        </Box>
                    </Box>

                    <Box
                        component="main"
                        id="innehall"
                        sx={{ flexGrow: 1, maxWidth: '46rem', minWidth: 0 }}
                    >
                        {dokument.avsnitt.map((avsnitt, index) => (
                            <Box
                                key={avsnitt.id}
                                component="section"
                                aria-labelledby={avsnitt.id}
                                sx={{ '& + &': { mt: 6 }, scrollMarginTop: 96 }}
                            >
                                <Typography
                                    component="h2"
                                    id={avsnitt.id}
                                    variant="h5"
                                    sx={{ scrollMarginTop: 96 }}
                                >
                                    {index + 1}. {avsnitt.rubrik}
                                </Typography>

                                {avsnitt.stycken?.map((stycke) => (
                                    <Typography key={stycke} variant="body1" sx={{ mt: 2 }}>
                                        {stycke}
                                    </Typography>
                                ))}

                                {avsnitt.punkter && (
                                    <Box
                                        component="ul"
                                        sx={{ mt: 2, mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                                    >
                                        {avsnitt.punkter.map((punkt) => (
                                            <Typography key={punkt} component="li" variant="body1">
                                                {punkt}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}

                                {avsnitt.tabell && (
                                    <TableContainer sx={{ mt: 3, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    {avsnitt.tabell.rubriker.map((rubrik) => (
                                                        <TableCell key={rubrik} sx={{ fontWeight: 'fontWeightSemiBold' }}>
                                                            {rubrik}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {avsnitt.tabell.rader.map((rad) => (
                                                    <TableRow key={rad[0]}>
                                                        {rad.map((cell) => (
                                                            <TableCell key={cell} sx={{ verticalAlign: 'top' }}>
                                                                {cell}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        ))}

                        <Divider sx={{ my: 6 }} />

                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Läs också{' '}
                            <Link component={RouterLink} href={syskon.href} color="text.primary">
                                {syskon.text}
                            </Link>
                            . Hittar du något som inte stämmer med hur tjänsten faktiskt fungerar är
                            det ett fel vi vill veta om —{' '}
                            <Link component={RouterLink} href={paths.rapporteraProblem} color="text.primary">
                                rapportera det här
                            </Link>
                            .
                        </Typography>
                    </Box>
                </Box>
            </Container>

            <Sidfot />
        </Box>
    );
}

const visuelltDoldTillsFokus = {
    position: 'absolute',
    left: -9999,
    top: 8,
    zIndex: 2000,
    px: 2,
    py: 1,
    borderRadius: 1,
    bgcolor: 'background.paper',
    color: 'text.primary',
    boxShadow: 3,
    '&:focus': { left: 16 }
} as const;
